<template>
	<div class="journey-layout">
		<div class="journey-steps" role="tablist" aria-label="How Paydeck works">
			<button v-for="(step, index) in journeySteps" :key="step.number" type="button" role="tab" :id="`journey-tab-${index}`" aria-controls="journey-panel" :aria-selected="active === index" :class="['journey-step', { active: active === index }]" @click="select(index)">
				<span class="journey-step-number">{{ step.number }}</span>
				<span class="journey-step-copy"><small>{{ step.label }}</small><strong>{{ step.title }}</strong><span>{{ step.detail }}</span></span>
				<ArrowUpRight :size="23" aria-hidden="true" />
				<i v-if="active === index" :class="playing && inView ? 'journey-step-timer playing' : 'journey-step-timer'" aria-hidden="true" />
			</button>
			<PdButton class="journey-cta" variant="ink" :href="registerUrl" :icon-right="ArrowUpRight">Create an account</PdButton>
		</div>
		<div class="journey-screen" id="journey-panel" role="tabpanel" :aria-labelledby="`journey-tab-${active}`">
			<div class="journey-screen-top"><span>PAYDECK / YOUR FLOW</span><span>{{ journeySteps[active].number }} / 03</span></div>
			<div class="journey-screen-stage" :key="active">
				<p class="pd-sr">{{ screenDescriptions[active] }}</p>
				<!-- A preview of the real app screens, built from the paydeck-ui components. Not interactive. -->
				<div class="journey-app" inert aria-hidden="true">
					<div class="journey-app-bar">
						<span class="journey-app-brand"><img src="/images/paydeck-icon-5.png" alt="" width="22" height="22" />paydeck</span>
						<PdBadge :tone="active === 2 ? 'success' : 'brand'" size="sm" plain>{{ appStages[active] }}</PdBadge>
					</div>

					<template v-if="active === 0">
						<PdEyebrow>Payment / New</PdEyebrow>
						<p class="journey-app-title">What are you paying for?</p>
						<div class="journey-app-options">
							<PdRadioCard v-for="option in purposeOptions" :key="option.name" :model-value="category" :value="option.name" name="journey-purpose" :title="option.name" :meta="option.detail" :icon="option.icon" />
						</div>
						<PdField label="Amount to pay"><PdAmountInput :model-value="25000" size="md" readonly :max="999999999" /></PdField>
					</template>

					<template v-if="active === 1">
						<PdEyebrow>Payment / Review</PdEyebrow>
						<p class="journey-app-title">Check before you pay.</p>
						<PdRecipientOption :beneficiary="sampleRecipient" :model-value="sampleRecipient.beneficiary_id" name="journey-recipient" />
						<PdKeyValue :items="reviewItems" total />
						<PdButton block spread :icon-right="ArrowRight">Confirm and pay</PdButton>
					</template>

					<template v-if="active === 2">
						<PdEyebrow>Payment / Receipt</PdEyebrow>
						<div class="journey-app-paid">
							<span class="journey-app-check"><Check :size="26" :stroke-width="3" /></span>
							<div><p class="journey-app-amount pd-num">{{ formatINR(25000) }}</p><p class="journey-app-sub">Paid to {{ sampleRecipient.bank_account_name }}</p></div>
						</div>
						<PdKeyValue :items="receiptItems" />
						<PdNotice tone="success" title="Saved to your payments">Find the reference and status any time in your account.</PdNotice>
					</template>
				</div>
			</div>
			<div class="journey-screen-bottom">
				<span>ILLUSTRATIVE EXPERIENCE · NO PAYMENT IS SENT</span>
				<div class="journey-route"><i :style="{ width: `${(active / 2) * 100}%` }" /></div>
				<span>{{ journeySteps[active].label }}</span>
			</div>
		</div>
	</div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { ArrowRight, ArrowUpRight, Check } from "lucide-vue-next";
import { categories, journeySteps } from "@/content/home";
import { registerUrl } from "@/content/site";
import PdButton from "@paydeck-ui/components/PdButton.vue";
import PdBadge from "@paydeck-ui/components/PdBadge.vue";
import PdEyebrow from "@paydeck-ui/components/PdEyebrow.vue";
import PdField from "@paydeck-ui/components/PdField.vue";
import PdAmountInput from "@paydeck-ui/components/PdAmountInput.vue";
import PdRadioCard from "@paydeck-ui/components/PdRadioCard.vue";
import PdKeyValue from "@paydeck-ui/components/PdKeyValue.vue";
import PdNotice from "@paydeck-ui/components/PdNotice.vue";
import PdRecipientOption from "@paydeck-ui/components/payments/PdRecipientOption.vue";
import { formatINR } from "@paydeck-ui/data/format.js";

const props = defineProps({ category: { type: String, required: true } });

const active = ref(0);
const playing = ref(true);
const inView = ref(false);
const chosen = computed(() => categories.find((item) => item.name === props.category) || categories[1]);

// Illustrative sample data for the preview screens (fictional recipient, mirrors paydeck-ui samples)
const sampleRecipient = { beneficiary_id: "b1", bank_account_name: "Example Home Properties", bank_name: "HDFC Bank", bank_branch_ifsc: "HDFC0000123", bank_account_number: "50100023452048", rant_id: "R1" };
const appStages = ["Step 1 of 3", "Step 2 of 3", "Settled"];
const screenDescriptions = [
	"Preview: choosing a payment purpose and entering an amount.",
	"Preview: reviewing a verified recipient, the amount and charges before confirming.",
	"Preview: a settled payment with its reference saved to your account."
];
const purposeOptions = computed(() => {
	const rest = categories.filter((item) => item.name !== chosen.value.name).slice(0, 2);
	return [chosen.value, ...rest];
});
const reviewItems = computed(() => [
	{ label: "Purpose", value: chosen.value.name, mono: false },
	{ label: "Charges", value: "Shown before you pay", mono: false },
	{ label: "Amount", value: formatINR(25000), strong: true }
]);
const receiptItems = computed(() => [
	{ label: "Purpose", value: chosen.value.name, mono: false },
	{ label: "Reference", value: "TD1789201001001" },
	{ label: "Settlement UTR", value: "HDFCN52026091200123" }
]);
let observer = null;
let timer = null;

function select(index) {
	active.value = index;
	playing.value = false;
}

onMounted(() => {
	const section = document.getElementById("how-it-works");
	if (!section || !("IntersectionObserver" in window)) return;
	observer = new IntersectionObserver(([entry]) => { inView.value = entry.isIntersecting; }, { threshold: 0.25 });
	observer.observe(section);
});

watch([inView, playing], () => {
	window.clearInterval(timer);
	if (!playing.value || !inView.value || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
	timer = window.setInterval(() => { active.value = (active.value + 1) % journeySteps.length; }, 6500);
});

onBeforeUnmount(() => {
	window.clearInterval(timer);
	if (observer) observer.disconnect();
});
</script>
